<script>
import axios from 'axios';
import { mapActions } from 'pinia';

import { useUserStore } from '../../../store/useUserStore.js';
import { logIn, signUp } from '../api/auth';
import auth from '../auth.js';
import CpButton from '../button';
import CpRegistrationFields from './registration-fields';
import CpSignInFields from './sign-in-fields';
import CpSocialButtons from './social-buttons';
import CpUnstyledButton from '../unstyled-button';
import CpRecaptcha from '../recaptcha';
import CpDialog from '../dialog';
import CpToasted from '../toasted';

export default {
  name: 'CpDialogAuthForm',
  components: {
    CpButton,
    CpRegistrationFields,
    CpSignInFields,
    CpSocialButtons,
    CpUnstyledButton,
    CpRecaptcha,
    CpDialog,
    CpToasted,
  },

  props: {
    showSignUp: {
      type: Boolean,
      default: false,
    },
    token: {
      type: String,
      default() {
        return this.$root.csrfToken;
      },
    },
    callbackAction: {
      type: Function,
      default: function () {},
    },
    handleCloseButton: {
      type: Function,
      default: function () {},
    },
    withArrow: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      registering: this.showSignUp,
      getUpdates: true,
      recaptchaVerification: null,
      recaptchaSiteKey: process.env.MIX_RECAPTCHA_PUBLIC_KEY,
      loading: false,
      provider: null,
      action: this.registering ? '/apiv2/user/store' : '/apiv2/user/login',
      toasted: [],
    };
  },
  created() {
    window.sessionStorage.removeItem('openWindowForSocialLogin');
  },
  methods: {
    toggleForm() {
      this.registering = !this.registering;
    },
    async handleProvider(provider) {
      this.provider = provider;
      const callback = async data => {
        await this.checkUserLogin(this.$root._handleCheckResponse);
        await this.handleSocialNetworkSignUp(data.isSignup === '1');
        this.callbackAction();
      };
      await auth.loginWithSocialMedia(provider, callback);
      this.handleCloseButton();
    },
    async handleFormSubmit() {
      if (!this.recaptchaVerification && this.$refs.recaptcha) {
        this.$refs.recaptcha.reset();
        this.$refs.recaptcha.execute();
        this.loading = false;
      } else {
        this.loading = true;
        try {
          const formData = new FormData(this.$refs.form);
          let response;
          if (this.registering) {
            response = await signUp(formData);
          } else {
            response = await logIn(formData);
          }
          await this.checkUserLogin(this.$root._handleCheckResponse);
          this.callbackAction();
          this.loading = false;
          this.handleCloseButton();
        } catch (error) {
          console.error('Form submission error:', error);
          this.createAlert({ message: error.message });
          this.loading = false;
        }
      }
    },
    createAlert({ message = '', theme = 'danger', duration = 5000 }) {
      this.toasted.push({ key: `alert-${new Date().toString()}`, message, theme, duration });
    },
    handleRecaptchaVerify(response) {
      this.loading = true;
      this.recaptchaVerification = response;
      this.handleFormSubmit();
    },
    handleRecaptchaExpired() {
      this.recaptchaVerification = null;
    },
    ...mapActions(useUserStore, ['checkUserLogin', 'handleSocialNetworkSignUp']),
  },
};
</script>

<template>
  <cp-dialog withoutScrolling :handleCloseButton="handleCloseButton" mandatoryAuthHeader>
    <div class="alerts">
      <cp-toasted v-for="toast in toasted" :key="toast.key" :message="toast.message" :theme="toast.theme" :duration="toast.duration" />
    </div>
    <div class="auth-form-container">
      <img v-if="withArrow" src="https://assets.crowdblue.com/v2/production/all/img/homepage/new-arrow1.svg" class="topArrow" />
      <img v-if="withArrow" src="https://assets.crowdblue.com/v2/production/all/img/homepage/new-arrow2.svg" class="leftArrow" />
      <form ref="form" class="auth-form formContainer" method="POST" :action="action">
        <input type="hidden" v-if="token" name="_token" :value="token" />
        <input type="hidden" name="remember" :value="1" />
        <input type="hidden" name="is_existing" :value="registering ? 0 : 1" />
        <input type="hidden" name="send_verification" :value="registering ? 1 : 0" />
        <input type="hidden" name="must_login" :value="registering ? 1 : 0" />
        <input type="hidden" name="is_provider" :value="provider ? 1 : 0" />
        <input type="hidden" name="provider" :value="provider" />
        <cp-recaptcha ref="recaptcha" :sitekey="recaptchaSiteKey" @verify="handleRecaptchaVerify" @expired="handleRecaptchaExpired">
          <button type="button" hidden>I'm not a robot</button>
        </cp-recaptcha>

        <component :is="registering ? 'cp-registration-fields' : 'cp-sign-in-fields'">
          <template v-if="registering">
            <h3 class="heading-2 neutral-2-color">Sign up with your networks</h3>
          </template>
          <template v-else-if="!registering">
            <h3 class="heading-2 neutral-2-color">Log in with your networks</h3>
          </template>
          <template #social-buttons>
            <cp-social-buttons :provider="provider" :handleProvider="handleProvider" />
            <p class="body neutral-2-color connectWith">Or continue with</p>
          </template>
        </component>

        <div class="button-container">
          <cp-button @click="handleFormSubmit" primary block :disabled="loading" :loading="loading">
            <h3 class="subhead">{{ registering ? 'Sign up' : 'Log in' }}</h3>
          </cp-button>
        </div>

        <p class="subheadline2 neutral-2-color">
          <template v-if="registering">
            You already have a CrowdBlue account?
            <cp-unstyled-button link @click="toggleForm">Log in.</cp-unstyled-button>
          </template>

          <template v-else>
            Don't have a CrowdBlue account?
            <cp-unstyled-button link @click="toggleForm">Sign up.</cp-unstyled-button>
          </template>
        </p>
      </form>
    </div>
  </cp-dialog>
</template>

<style scoped>
.auth-form-container {
  z-index: 1;
  position: relative;
  max-height: 600px;
  overflow: auto;
}

.auth-form {
  color: #787878;
  font: lighter 16px var(--serif-font);
  padding: 22px;
  width: 400px;
}
.auth-form-container :deep(input.text-input),
.auth-form-container :deep(input.form-input) {
  appearance: none;
  -webkit-appearance: none;
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  background: transparent !important;
  margin-bottom: 0 !important;
  height: auto;
}
.auth-form-container :deep(input.text-input:focus),
.auth-form-container :deep(input.form-input:focus) {
  border-color: transparent !important;
  box-shadow: none !important;
  color: #000;
}
.auth-form-container :deep(.form-input) {
  font: inherit;
  font-size: 21px;
}

@media screen and (max-height: 800px) and (max-width: 1200px) and (min-width: 800px) {
  .auth-form-container {
    max-height: 500px;
  }
}

@media (max-width: 768px) {
  .auth-form {
    width: auto;
  }
}

>>> .form-header {
  text-align: center;
}

>>> .form-title {
  color: #121212;
  font-size: 40px;
  line-height: 1;
  margin: 20px 0;
}

>>> .field {
  margin: 25px 0;
}

>>> .field-label {
  color: #121212;
  font: bold 1em var(--sans-font);
  margin: 3px 0;
}

>>> .checkbox-label {
  display: flex;
}

>>> .checkbox-label input {
  flex-shrink: 0;
}

>>> .form-input {
  font: inherit;
  font-size: 21px;
}

>>> .button-container {
  margin: 20px 0;
}

>>> .form-footer {
  font-size: 14px;
  text-align: center;
}

.formContainer {
  background: var(--white);
  border: 1px solid var(--secondary-1);
  box-shadow: 0px 0px 14px 0px #00000026;
  border-radius: 8px;
  padding: 16px 16px 40px;
}

.connectWith {
  position: relative;
  text-align: center;
}

.connectWith:before {
  content: '';
  display: block;
  width: 30%;
  height: 1px;
  background: var(--neutral-1);
  left: 0;
  top: 50%;
  position: absolute;
}

.connectWith:after {
  content: '';
  display: block;
  width: 30%;
  height: 1px;
  background: var(--neutral-1);
  right: 0;
  top: 50%;
  position: absolute;
}

.topArrow,
.leftArrow {
  position: absolute;
  top: -10%;
  z-index: -1;
}

.topArrow {
  top: -10%;
}

.leftArrow {
  left: -35%;
  top: 55%;
}

@media screen and (max-width: 768px) {
  .topArrow,
  .leftArrow {
    display: none;
  }
}
</style>
